/**
 * App entry point.
 * Flow: get container → init story engine → init UI → scroll tracking & restoration → state →
 *       build screen nav deps & setup callbacks → show initial screen.
 */
import './styles/main.scss'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { initAnimationSettings } from './utils/animation-settings'
import { StoryEngine } from './features/story-engine'
import { buildStoryEngineConfig } from './core/story-engine-config'
import { initializeUI } from './core/app-orchestrator'
import { setupScrollTracking } from './core/scroll-tracking'
import { setupScrollRestoration } from './features/scroll-restoration'
import type { LenisInstance } from './features/lenis/types'
import type { SceneData } from './features/scenes/types'
import { showGameScreen, showHomeScreen, restartGame, buildScreenNavDependencies } from './core/screen-navigation'
import { setupAppCallbacks } from './core/app-callbacks'
import { debugLog } from './features/debug'
import { setupEpilogue } from './features/epilogue'
import { initContest } from './features/contest'
import { Howler } from 'howler'
import { SfxManager, type UiSfxConfig } from './features/audio/SfxManager'
import { initAudioManager, getVolumeCeilings, getSettingsDefaults, type AudioPatternsConfig } from './features/audio'
import { ensureUnlockListeners } from './features/audio/unlock-audio-context'
import storyData from './config/story.json'
import { applyResponsiveImage } from './utils/pathResolver'

gsap.registerPlugin(ScrollTrigger)
initAnimationSettings()

document.querySelectorAll<HTMLImageElement>('img[src*="/assets/"]').forEach(applyResponsiveImage)

document.addEventListener('visibilitychange', () => {
  Howler.mute(document.hidden)
})

const app = document.querySelector<HTMLDivElement>('#app')
if (app) initApp()

function initApp(): void {
  const audioConfigRaw = (storyData as unknown as { config?: { audio?: { global?: { outro?: string }; patterns?: AudioPatternsConfig } } })
    ?.config?.audio
  initAudioManager(undefined, audioConfigRaw?.patterns)
  ensureUnlockListeners()

  const gameScreen = getGameScreen(app!)
  const storyEngine = initStoryEngine(gameScreen, audioConfigRaw)
  const epilogue = setupEpilogue(gameScreen, audioConfigRaw?.global?.outro)

  storyEngine.setOnEpilogueScene((sceneId, element, container) => epilogue.appendElement(sceneId, element, container))
  storyEngine.setOnBeforeReset(() => epilogue.remove())
  const ui = initializeUI(app!, storyData.scenes)
  const scrollTracking = initScrollTracking(storyEngine)
  const scrollRestoration = setupScrollRestoration()

  const uiSfxConfig = (storyData as unknown as { config?: { audio?: { ui?: UiSfxConfig } } })
    ?.config?.audio?.ui ?? { buttonClick: 'button-click.mp3', choice: 'choice.mp3' }
  const sfxManager = new SfxManager(uiSfxConfig)
  sfxManager.init()

  {
    const ceilings = getVolumeCeilings()
    const defaults = getSettingsDefaults()
    let userSound = defaults.sound
    try {
      const raw = localStorage.getItem('rychle-sipy-a-velikonocni-poklad:settings')
      if (raw) {
        const parsed = JSON.parse(raw)
        userSound = parsed?.sound?.sound ?? userSound
      }
    } catch { /* ignore */ }
    sfxManager.setVolume((userSound / 100) * ceilings.sound)
  }

  const state = {
    lenisInstance: null as LenisInstance | null,
    isTransitioning: { value: false },
    isRestoringScroll: { value: false },
    globalRainManager: null as ReturnType<
      typeof import('./features/rain/global-rain-manager').createGlobalRainManager
    > | null,
    atmoManager: null as import('./features/audio/AtmoManager').AtmoManager | null,
    musicManager: null as import('./features/audio/AtmoManager').AtmoManager | null,
    scrollSfxManager: null as import('./features/audio/ScrollSfxManager').ScrollSfxManager | null,
    sfxManager,
  }
  ;(window as any).__appState = state

  setupStoryEngineStateChange(storyEngine, ui, state.isTransitioning)

  const getDeps = () =>
    buildScreenNavDependencies(app!, state, gameScreen, storyEngine, scrollTracking, scrollRestoration, ui)

  setupAppCallbacks({
    storyEngine,
    ui,
    showGameScreen: (loadState, updateButtons) => showGameScreen(getDeps(), loadState, updateButtons),
    showHomeScreen: () => showHomeScreen(getDeps()),
    isTransitioning: state.isTransitioning,
  })

  initContest()
  bindRestartButton(getDeps, ui)
  showInitialScreen(ui)
}

function getGameScreen(app: HTMLElement): HTMLDivElement {
  const gameScreen = app.querySelector('.game-screen') as HTMLDivElement | null
  if (!gameScreen) throw new Error('Game screen element not found in HTML')
  return gameScreen
}

function initStoryEngine(gameScreen: HTMLDivElement, audioConfig?: Record<string, unknown>): StoryEngine {
  debugLog('[main] Creating storyEngine')
  const storyEngineConfig = buildStoryEngineConfig(storyData)
  const storyEngine = new StoryEngine(gameScreen, storyData.scenes as SceneData[], storyEngineConfig)
  debugLog('[main] storyEngine created', { storyEngine: !!storyEngine })
  if (audioConfig) {
    const effectsRain = (storyData as unknown as { config?: { effects?: { rain?: { from?: string[] } } } })
      ?.config?.effects?.rain
    const preloadConfig = { ...audioConfig, rainStartScene: effectsRain?.from?.[0] }
    storyEngine.setAudioPreloadConfig(preloadConfig)
  }
  storyEngine.preloadInitialScenes()
  return storyEngine
}

function initScrollTracking(storyEngine: StoryEngine) {
  return setupScrollTracking(null, {
    onScroll: (scrollY) => {
      storyEngine.saveCurrentState(scrollY)
    },
  })
}

function setupStoryEngineStateChange(
  storyEngine: StoryEngine,
  ui: ReturnType<typeof initializeUI>,
  isTransitioning: { value: boolean },
): void {
  storyEngine.setOnStateChange(() => {
    if (!isTransitioning.value) {
      ui.homeScreen.updateButtons()
    }
  })
}

function bindRestartButton(getDeps: () => ReturnType<typeof buildScreenNavDependencies>, ui: ReturnType<typeof initializeUI>): void {
  const btn = document.querySelector<HTMLButtonElement>('[data-action="restart-game"]')
  btn?.addEventListener('click', () => {
    ui.confirmationModal.show(
      'Začít znovu?',
      'Spuštění nové hry vymaže veškerý uložený postup.',
      ['Zpět', 'Začít znovu'],
      () => restartGame(getDeps()),
      () => {},
    )
  })
}

function showInitialScreen(ui: ReturnType<typeof initializeUI>): void {
  ui.homeScreen.show()
  ui.gameNavigation.show(false, false)
}
